import gsap from "gsap";
import type { HistoricalTimelineDecade } from "../generated-types";
import {
	getDetailCardFactsMarkup,
	renderDecadeColumn,
	syncHoverImageMedia,
	type DecadeColumnLabels,
} from "./decade-column-markup";
import { createYearDots } from "./create-year-dots";
import { renderDecadeHoverGraphsHost } from "./decade-hover-graph-animations";
import {
	EXPAND_DURATION_MS,
	GRID_ENTRANCE_HR_DURATION_S,
	GRID_ENTRANCE_HR_EASE,
	HOVER_EXPLORE_DURATION_S,
	HOVER_EXPLORE_EXTRA_DELAY_S,
	HOVER_FACTS_DURATION_S,
	HOVER_FACTS_EXIT_DURATION_S,
	HOVER_FACTS_EXIT_STAGGER_S,
	HOVER_FACTS_EXTRA_DELAY_S,
	HOVER_FACTS_STAGGER_S,
	HOVER_FACTS_Y_PX,
	HOVER_PARALLAX_DURATION_S,
	HOVER_PARALLAX_EASE,
	HOVER_PARALLAX_IMAGE_SCALE,
	HOVER_PARALLAX_MAX_ROTATE_DEG,
	HOVER_PARALLAX_MAX_TRANSLATE_PX,
	HOVER_SELECT_FADE_DURATION_S,
	HOVER_SELECT_FOLLOW_DURATION_S,
	HOVER_SELECT_FOLLOW_EASE,
	MOBILE_HOVER_DOT_COLS,
	DESKTOP_HOVER_DOT_COLS,
	MOBILE_OVERVIEW_HOVER_REVEAL_DURATION_MS,
} from "./timeline-constants";
import {
	getDecadeHoverImageUrl,
	getDotItems,
	getDecadeYearRange,
	getVisibleYearNames,
	getVisibleYearRange,
	getFeaturedEvent,
	prefersReducedMotion,
	seededRandom,
	type TimelineDotItem,
} from "./timeline-utils";
import { isMobileOverview } from "./create-overview-mobile-swiper";

export type { DecadeColumnLabels } from "./decade-column-markup";

export type DecadeColumnController = {
	element: HTMLElement;
	update: (options: {
		decade?: HistoricalTimelineDecade;
		isHovered: boolean;
		hoverScatterToken: number;
		gridEntranceToken: number;
		activeCategoryKeys: string[];
	}) => void;
	destroy: () => void;
};

export function animateDividerEntrance(divider: HTMLElement): gsap.core.Tween | undefined {
	if (prefersReducedMotion()) {
		gsap.set(divider, { scaleY: 1 });
		return undefined;
	}

	gsap.set(divider, { scaleY: 0, transformOrigin: "bottom center" });
	return gsap.to(divider, {
		scaleY: 1,
		duration: GRID_ENTRANCE_HR_DURATION_S,
		ease: GRID_ENTRANCE_HR_EASE,
		overwrite: "auto",
	});
}

function animateExploreLabel(container: HTMLElement): gsap.core.Tween | undefined {
	const elements = [
		container.querySelector<HTMLElement>("[data-explore-line]"),
		container.querySelector<HTMLElement>("[data-years-line]"),
	].filter((element): element is HTMLElement => element !== null);

	if (elements.length === 0) {
		return undefined;
	}

	if (prefersReducedMotion()) {
		gsap.set(elements, { opacity: 1 });
		return undefined;
	}

	const baseDelay = (EXPAND_DURATION_MS * 0.3) / 1000 + HOVER_EXPLORE_EXTRA_DELAY_S;
	gsap.set(elements, { opacity: 0 });
	return gsap.to(elements, {
		opacity: 1,
		duration: HOVER_EXPLORE_DURATION_S,
		delay: baseDelay,
		ease: "power2.out",
		overwrite: "auto",
	});
}

function animateFactRows(container: HTMLElement): gsap.core.Tween | undefined {
	const elements = Array.from(container.querySelectorAll<HTMLElement>("[data-fact-row]"));
	if (elements.length === 0) {
		return undefined;
	}

	if (prefersReducedMotion()) {
		gsap.set(elements, { opacity: 1, y: 0 });
		return undefined;
	}

	const baseDelay = (EXPAND_DURATION_MS * 0.3) / 1000 + HOVER_FACTS_EXTRA_DELAY_S;
	gsap.set(elements, { opacity: 0, y: HOVER_FACTS_Y_PX });
	return gsap.to(elements, {
		opacity: 1,
		y: 0,
		duration: HOVER_FACTS_DURATION_S,
		stagger: HOVER_FACTS_STAGGER_S,
		delay: baseDelay,
		ease: "power2.out",
		overwrite: "auto",
	});
}

function fadeOutFactRows(container: HTMLElement): gsap.core.Tween | undefined {
	const elements = Array.from(container.querySelectorAll<HTMLElement>("[data-fact-row]"));
	if (elements.length === 0) {
		return undefined;
	}

	if (prefersReducedMotion()) {
		gsap.set(elements, { opacity: 0, y: HOVER_FACTS_Y_PX });
		return undefined;
	}

	return gsap.to(elements, {
		opacity: 0,
		y: HOVER_FACTS_Y_PX * 0.5,
		duration: HOVER_FACTS_EXIT_DURATION_S,
		stagger: HOVER_FACTS_EXIT_STAGGER_S,
		ease: "power2.in",
		overwrite: "auto",
	});
}

function bindSelectLabel(
	column: HTMLElement,
	selectLabel: HTMLElement,
	selectContent: HTMLElement
): {
	setActive: (active: boolean, pointer?: { x: number; y: number }) => void;
	syncPointer: (clientX: number, clientY: number, immediate?: boolean) => void;
	destroy: () => void;
} {
	let isActive = false;
	let followEnabled = false;
	let lastPointer = { x: 0, y: 0 };
	let fadeTween: gsap.core.Tween | undefined;
	let xTo: gsap.QuickToFunc | undefined;
	let yTo: gsap.QuickToFunc | undefined;
	let expandPositionTimer: ReturnType<typeof setTimeout> | null = null;

	gsap.set(selectContent, { scale: 0, opacity: 0, transformOrigin: "center center" });

	const clearExpandPositionTimer = () => {
		if (expandPositionTimer) {
			clearTimeout(expandPositionTimer);
			expandPositionTimer = null;
		}
	};

	const resetSelectLabelPosition = () => {
		gsap.set(selectLabel, { x: 0, y: 0 });
	};

	const syncPosition = (clientX: number, clientY: number, immediate = false) => {
		const rect = column.getBoundingClientRect();
		const x = clientX - rect.left - selectLabel.offsetWidth / 2;
		const y = clientY - rect.top - selectLabel.offsetHeight / 2;

		if (immediate || prefersReducedMotion()) {
			gsap.set(selectLabel, { x, y });
			return;
		}

		xTo?.(x);
		yTo?.(y);
	};

	const revealSelectLabel = () => {
		if (!isActive) {
			return;
		}

		const followDuration = HOVER_SELECT_FOLLOW_DURATION_S;
		xTo = gsap.quickTo(selectLabel, "x", { duration: followDuration, ease: HOVER_SELECT_FOLLOW_EASE });
		yTo = gsap.quickTo(selectLabel, "y", { duration: followDuration, ease: HOVER_SELECT_FOLLOW_EASE });

		syncPosition(lastPointer.x, lastPointer.y, true);

		followEnabled = true;

		fadeTween?.kill();
		fadeTween = gsap.fromTo(
			selectContent,
			{ scale: 0, opacity: 0, transformOrigin: "center center" },
			{
				scale: 1,
				opacity: 1,
				duration: HOVER_SELECT_FADE_DURATION_S,
				ease: "power2.out",
				overwrite: "auto",
			}
		);
	};

	const setActive = (nextActive: boolean, pointer?: { x: number; y: number }) => {
		if (isActive === nextActive) {
			return;
		}

		isActive = nextActive;
		fadeTween?.kill();
		clearExpandPositionTimer();
		followEnabled = false;

		if (prefersReducedMotion()) {
			if (!nextActive) {
				gsap.killTweensOf(selectLabel);
				resetSelectLabelPosition();
			}

			gsap.set(selectContent, { scale: nextActive ? 1 : 0, opacity: nextActive ? 1 : 0 });

			if (nextActive) {
				followEnabled = true;
				if (pointer) {
					syncPosition(pointer.x, pointer.y, true);
				}
			}

			return;
		}

		if (nextActive) {
			if (pointer) {
				lastPointer = pointer;
			}

			gsap.set(selectContent, { scale: 0, opacity: 0, transformOrigin: "center center" });
			resetSelectLabelPosition();

			expandPositionTimer = setTimeout(() => {
				expandPositionTimer = null;
				revealSelectLabel();
			}, EXPAND_DURATION_MS);
			return;
		}

		gsap.killTweensOf(selectLabel);
		xTo = undefined;
		yTo = undefined;

		fadeTween = gsap.to(selectContent, {
			scale: 0,
			opacity: 0,
			transformOrigin: "center center",
			duration: HOVER_SELECT_FADE_DURATION_S,
			ease: "power2.in",
			overwrite: "auto",
			onComplete: () => {
				if (!isActive) {
					resetSelectLabelPosition();
				}
			},
		});
	};

	const syncPointer = (clientX: number, clientY: number, immediate = false) => {
		lastPointer = { x: clientX, y: clientY };

		if (!isActive || !followEnabled) {
			return;
		}

		syncPosition(clientX, clientY, immediate);
	};

	const handlePointerMove = (event: MouseEvent) => {
		syncPointer(event.clientX, event.clientY);
	};

	column.addEventListener("mousemove", handlePointerMove);

	return {
		setActive,
		syncPointer,
		destroy: () => {
			clearExpandPositionTimer();
			column.removeEventListener("mousemove", handlePointerMove);
			fadeTween?.kill();
			gsap.killTweensOf(selectLabel);
			gsap.killTweensOf(selectContent);
		},
	};
}

type ParallaxProfile = {
	xFromX: number;
	xFromY: number;
	yFromY: number;
	yFromX: number;
	rotXFromY: number;
	rotXFromX: number;
	rotYFromX: number;
	rotYFromY: number;
	xDuration: number;
	yDuration: number;
	rotateXDuration: number;
	rotateYDuration: number;
};

type ParallaxRuntime = {
	image: HTMLElement;
	profile: ParallaxProfile;
	xTo: gsap.QuickToFunc;
	yTo: gsap.QuickToFunc;
	rotationXTo: gsap.QuickToFunc;
	rotationYTo: gsap.QuickToFunc;
};

function getParallaxProfile(decadeIndex: number): ParallaxProfile {
	const base = decadeIndex * 19 + 41;
	const mix = (seed: number, min: number, max: number) => min + seededRandom(seed) * (max - min);

	return {
		xFromX: mix(base, 0.55, 1),
		xFromY: mix(base + 1, -0.75, 0.75),
		yFromY: mix(base + 2, 0.55, 1),
		yFromX: mix(base + 3, -0.75, 0.75),
		rotXFromY: mix(base + 4, 0.45, 1),
		rotXFromX: mix(base + 5, -0.55, 0.55),
		rotYFromX: mix(base + 6, 0.45, 1),
		rotYFromY: mix(base + 7, -0.55, 0.55),
		xDuration: HOVER_PARALLAX_DURATION_S * mix(base + 8, 0.88, 1.18),
		yDuration: HOVER_PARALLAX_DURATION_S * mix(base + 9, 0.92, 1.22),
		rotateXDuration: HOVER_PARALLAX_DURATION_S * mix(base + 10, 0.9, 1.15),
		rotateYDuration: HOVER_PARALLAX_DURATION_S * mix(base + 11, 0.94, 1.2),
	};
}

function createParallaxRuntime(hoverImage: HTMLElement, profile: ParallaxProfile): ParallaxRuntime {
	gsap.set(hoverImage, {
		scale: HOVER_PARALLAX_IMAGE_SCALE,
		x: 0,
		y: 0,
		rotationX: 0,
		rotationY: 0,
		transformOrigin: "center center",
		force3D: true,
	});

	return {
		image: hoverImage,
		profile,
		xTo: gsap.quickTo(hoverImage, "x", { duration: profile.xDuration, ease: HOVER_PARALLAX_EASE }),
		yTo: gsap.quickTo(hoverImage, "y", { duration: profile.yDuration, ease: HOVER_PARALLAX_EASE }),
		rotationXTo: gsap.quickTo(hoverImage, "rotationX", {
			duration: profile.rotateXDuration,
			ease: HOVER_PARALLAX_EASE,
		}),
		rotationYTo: gsap.quickTo(hoverImage, "rotationY", {
			duration: profile.rotateYDuration,
			ease: HOVER_PARALLAX_EASE,
		}),
	};
}

function resetParallaxRuntime(runtime: ParallaxRuntime): void {
	runtime.xTo(0);
	runtime.yTo(0);
	runtime.rotationXTo(0);
	runtime.rotationYTo(0);
}

function bindHoverParallax(hoverCard: HTMLElement, decadeIndex: number): {
	setActive: (active: boolean) => void;
	syncPointer: (clientX: number, clientY: number) => void;
	refresh: () => void;
	destroy: () => void;
} {
	if (prefersReducedMotion()) {
		return {
			setActive: () => undefined,
			syncPointer: () => undefined,
			refresh: () => undefined,
			destroy: () => undefined,
		};
	}

	const profile = getParallaxProfile(decadeIndex);
	let runtime: ParallaxRuntime | null = null;
	let isActive = false;
	let followEnabled = false;
	let expandPositionTimer: ReturnType<typeof setTimeout> | null = null;
	let pendingPointer: { x: number; y: number } | null = null;

	const clearExpandPositionTimer = () => {
		if (expandPositionTimer) {
			clearTimeout(expandPositionTimer);
			expandPositionTimer = null;
		}
	};

	const teardownRuntime = () => {
		if (runtime) {
			resetParallaxRuntime(runtime);
			runtime = null;
		}
	};

	const ensureRuntime = (): ParallaxRuntime | null => {
		const hoverImage = hoverCard.querySelector<HTMLElement>(".historical-timeline__decade-hover-image");
		if (!hoverImage) {
			teardownRuntime();
			return null;
		}

		if (runtime?.image === hoverImage) {
			return runtime;
		}

		teardownRuntime();
		runtime = createParallaxRuntime(hoverImage, profile);
		return runtime;
	};

	const applyPointer = (clientX: number, clientY: number) => {
		const activeRuntime = ensureRuntime();
		if (!activeRuntime) {
			return;
		}

		const rect = hoverCard.getBoundingClientRect();
		if (rect.width <= 0 || rect.height <= 0) {
			return;
		}

		const normX = gsap.utils.clamp(
			-1,
			1,
			(clientX - (rect.left + rect.width / 2)) / (rect.width / 2)
		);
		const normY = gsap.utils.clamp(
			-1,
			1,
			(clientY - (rect.top + rect.height / 2)) / (rect.height / 2)
		);

		const translateX =
			-(normX * profile.xFromX + normY * profile.xFromY) * HOVER_PARALLAX_MAX_TRANSLATE_PX;
		const translateY =
			-(normY * profile.yFromY + normX * profile.yFromX) * HOVER_PARALLAX_MAX_TRANSLATE_PX;
		const rotateX =
			(normY * profile.rotXFromY + normX * profile.rotXFromX) * HOVER_PARALLAX_MAX_ROTATE_DEG;
		const rotateY =
			-(normX * profile.rotYFromX + normY * profile.rotYFromY) * HOVER_PARALLAX_MAX_ROTATE_DEG;

		activeRuntime.xTo(translateX);
		activeRuntime.yTo(translateY);
		activeRuntime.rotationXTo(rotateX);
		activeRuntime.rotationYTo(rotateY);
	};

	const resetParallax = () => {
		const activeRuntime = runtime ?? ensureRuntime();
		if (!activeRuntime) {
			return;
		}

		resetParallaxRuntime(activeRuntime);
	};

	const syncPointer = (clientX: number, clientY: number) => {
		pendingPointer = { x: clientX, y: clientY };

		if (!isActive || !followEnabled) {
			return;
		}

		applyPointer(clientX, clientY);
	};

	const setActive = (nextActive: boolean) => {
		if (isActive === nextActive) {
			return;
		}

		isActive = nextActive;
		clearExpandPositionTimer();
		followEnabled = false;

		if (!nextActive) {
			pendingPointer = null;
			resetParallax();
			return;
		}

		expandPositionTimer = setTimeout(() => {
			expandPositionTimer = null;
			followEnabled = true;

			if (pendingPointer) {
				applyPointer(pendingPointer.x, pendingPointer.y);
			}
		}, EXPAND_DURATION_MS);
	};

	ensureRuntime();

	return {
		setActive,
		syncPointer,
		refresh: () => {
			teardownRuntime();
			ensureRuntime();
			if (isActive && followEnabled && pendingPointer) {
				applyPointer(pendingPointer.x, pendingPointer.y);
			}
		},
		destroy: () => {
			clearExpandPositionTimer();
			teardownRuntime();
		},
	};
}

export type DecadeColumnA11yCallbacks = {
	onFocus?: () => void;
	onNavigatePrevious?: () => void;
	onNavigateNext?: () => void;
	shouldHandleSelect?: () => boolean;
	activateOnFirstClick?: () => boolean;
};

export function createDecadeColumn(
	decade: HistoricalTimelineDecade,
	decadeIndex: number,
	labels: DecadeColumnLabels,
	onSelect: () => void,
	a11y?: DecadeColumnA11yCallbacks
): DecadeColumnController {
	let decadeData = decade;
	const {
		column,
		defaultDivider,
		defaultBody,
		title,
		hoverCard,
		exploreLabel,
		hoverDotsHost,
		graphsHost,
		factsHost,
		selectLabel,
		selectContent,
	} = renderDecadeColumn({ decade, decadeIndex, labels });

	const getDotId = (yearIndex: number) => decadeIndex * 1000 + yearIndex;

	const syncExploreYearLabel = (dotItems: TimelineDotItem[]) => {
		const yearsLine = exploreLabel.querySelector<HTMLElement>("[data-years-line]");
		if (yearsLine) {
			yearsLine.textContent = getVisibleYearRange(dotItems) || getDecadeYearRange(decadeData);
		}

		const visibleYearCount = getVisibleYearNames(dotItems).length;
		const decadeYearCount = (decadeData.years ?? [])
			.map((year) => year.name?.trim())
			.filter(Boolean).length;

		exploreLabel.classList.toggle("is-single-year", visibleYearCount === 1);
		exploreLabel.classList.toggle(
			"is-no-year-dots",
			dotItems.length === 0 && decadeYearCount === 1
		);
	};

	let defaultDotsCleanup: () => void = () => {};
	let hoverDotsCleanup: () => void = () => {};
	let dividerTween: gsap.core.Tween | undefined;
	let exploreTween: gsap.core.Tween | undefined;
	let factsTween: gsap.core.Tween | undefined;
	let graphDrawTween: gsap.core.Timeline | undefined;
	let lastGridEntranceToken = 0;
	let lastActiveCategoryKey = "";
	let lastHoverImageKey = "";
	let lastHoverContentKey = "";

	const handleClick = () => {
		if (a11y?.shouldHandleSelect && !a11y.shouldHandleSelect()) {
			return;
		}

		if (!column.classList.contains("is-hovered")) {
			if (a11y?.activateOnFirstClick?.()) {
				a11y.onFocus?.();
			}

			return;
		}

		onSelect();
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === "ArrowRight" || event.key === "ArrowDown") {
			event.preventDefault();
			a11y?.onNavigateNext?.();
			return;
		}

		if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
			event.preventDefault();
			a11y?.onNavigatePrevious?.();
			return;
		}

		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			onSelect();
		}
	};

	const handleFocus = () => {
		if (a11y?.activateOnFirstClick?.()) {
			return;
		}

		a11y?.onFocus?.();
	};

	column.addEventListener("click", handleClick);
	column.addEventListener("keydown", handleKeyDown);
	column.addEventListener("focus", handleFocus);

	const selectLabelController = bindSelectLabel(column, selectLabel, selectContent);
	const parallaxController = bindHoverParallax(hoverCard, decadeIndex);
	let lastPointer = { x: 0, y: 0 };

	column.addEventListener("mouseenter", (event) => {
		lastPointer = { x: event.clientX, y: event.clientY };
	});

	column.addEventListener("mousemove", (event) => {
		lastPointer = { x: event.clientX, y: event.clientY };
		parallaxController.syncPointer(event.clientX, event.clientY);
	});

	const renderDefaultDots = (
		dotItems: TimelineDotItem[],
		gridEntranceToken: number
	) => {
		defaultDotsCleanup();
		defaultBody.querySelector(".historical-timeline__dots")?.remove();

		const { element, cleanup } = createYearDots({
			dotItems,
			scatterToken: gridEntranceToken > 0 ? gridEntranceToken : undefined,
			scatterVariant: "lite",
			getDotId,
		});

		defaultDotsCleanup = cleanup;
		defaultBody.appendChild(element);
	};

	let lastGraphActiveKey = "";

	const getGraphAnimationBaseDelayS = (shouldAnimate: boolean): number | undefined => {
		if (!shouldAnimate) {
			return undefined;
		}

		if (isMobileOverview()) {
			return MOBILE_OVERVIEW_HOVER_REVEAL_DURATION_MS / 1000;
		}

		return (EXPAND_DURATION_MS * 0.25) / 1000;
	};

	const renderHoverGraphs = (activeCategoryKeys: string[], shouldAnimate: boolean) => {
		graphDrawTween?.kill();
		graphDrawTween = renderDecadeHoverGraphsHost(
			graphsHost,
			decadeData,
			activeCategoryKeys,
			`historical-timeline-graph-${decadeIndex}`,
			shouldAnimate,
			getGraphAnimationBaseDelayS(shouldAnimate)
		);
		lastGraphActiveKey = activeCategoryKeys.join(",");
	};

	const renderHoverContent = (
		dotItems: TimelineDotItem[],
		hoverScatterToken: number,
		activeCategoryKeys: string[]
	) => {
		syncExploreYearLabel(dotItems);

		factsHost.innerHTML = getDetailCardFactsMarkup(decadeData, activeCategoryKeys, labels);

		renderHoverGraphs(activeCategoryKeys, hoverScatterToken > 0);

		hoverDotsCleanup();
		hoverDotsHost.replaceChildren();

		if (hoverScatterToken > 0 && dotItems.length > 0) {
			const { element, cleanup } = createYearDots({
				dotItems,
				scatterToken: hoverScatterToken,
				scatterBaseDelay: (EXPAND_DURATION_MS * 0.3) / 1000,
				proximityStage: column,
				getDotId,
				dotCols: isMobileOverview() ? MOBILE_HOVER_DOT_COLS : DESKTOP_HOVER_DOT_COLS,
			});

			hoverDotsCleanup = cleanup;
			hoverDotsHost.appendChild(element);
		}

		exploreTween?.kill();
		factsTween?.kill();

		if (hoverScatterToken > 0) {
			exploreTween = animateExploreLabel(exploreLabel);
			factsTween = animateFactRows(factsHost);
		}

		title.classList.toggle("is-dimmed", dotItems.length === 0);
	};

	renderHoverGraphs([], false);

	const update = ({
		decade: nextDecade,
		isHovered,
		hoverScatterToken,
		gridEntranceToken,
		activeCategoryKeys,
	}: {
		decade?: HistoricalTimelineDecade;
		isHovered: boolean;
		hoverScatterToken: number;
		gridEntranceToken: number;
		activeCategoryKeys: string[];
	}) => {
		if (nextDecade) {
			decadeData = nextDecade;
		}

		const dotItems = getDotItems(decadeData, activeCategoryKeys);
		const activeKey = activeCategoryKeys.join(",");
		const hoverImageUrl = getDecadeHoverImageUrl(decadeData, dotItems);
		const hoverImageKey = `${hoverImageUrl ?? ""}|${decadeData.name ?? ""}|${activeKey}`;

		if (hoverImageKey !== lastHoverImageKey) {
			lastHoverImageKey = hoverImageKey;
			const featuredEvent = getFeaturedEvent(decadeData, dotItems);
			const needsParallaxRefresh = syncHoverImageMedia(
				hoverCard,
				hoverImageUrl,
				decadeData.name ?? featuredEvent?.name ?? ""
			);

			if (needsParallaxRefresh) {
				parallaxController.refresh();
			}
		}

		column.classList.toggle("is-hovered", isHovered);
		title.classList.toggle("is-dimmed", dotItems.length === 0);

		syncExploreYearLabel(dotItems);

		const hoverContentKey = `${hoverScatterToken}|${activeKey}|${dotItems.length}`;
		const isMobile = isMobileOverview();

		if (isHovered && hoverScatterToken > 0 && hoverContentKey !== lastHoverContentKey) {
			lastHoverContentKey = hoverContentKey;
			renderHoverContent(dotItems, hoverScatterToken, activeCategoryKeys);
		} else if (!isHovered) {
			lastHoverContentKey = "";
			exploreTween?.kill();
			factsTween?.kill();
			factsTween = fadeOutFactRows(factsHost);
		} else if (isHovered && activeKey !== lastGraphActiveKey) {
			renderHoverGraphs(activeCategoryKeys, hoverScatterToken > 0);
			factsHost.innerHTML = getDetailCardFactsMarkup(decadeData, activeCategoryKeys, labels);
		}

		selectLabelController.setActive(isHovered && !isMobile, isHovered && !isMobile ? lastPointer : undefined);
		parallaxController.setActive(isHovered && !isMobile);

		if (isHovered && !isMobile) {
			parallaxController.syncPointer(lastPointer.x, lastPointer.y);
		}

		const shouldRefreshDefault =
			gridEntranceToken !== lastGridEntranceToken || activeKey !== lastActiveCategoryKey;

		if (shouldRefreshDefault) {
			if (gridEntranceToken !== lastGridEntranceToken) {
				dividerTween?.kill();
				dividerTween = animateDividerEntrance(defaultDivider);
			}

			lastGridEntranceToken = gridEntranceToken;
			lastActiveCategoryKey = activeKey;
			renderDefaultDots(dotItems, gridEntranceToken);
		}
	};

	return {
		element: column,
		update,
		destroy: () => {
			column.removeEventListener("click", handleClick);
			column.removeEventListener("keydown", handleKeyDown);
			column.removeEventListener("focus", handleFocus);
			selectLabelController.destroy();
			parallaxController.destroy();
			defaultDotsCleanup();
			hoverDotsCleanup();
			dividerTween?.kill();
			exploreTween?.kill();
			factsTween?.kill();
			graphDrawTween?.kill();
		},
	};
}
